import { useEffect, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, CalendarDays, Check, Copy as CopyIcon, Eye, FileQuestion, Loader2, Share2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import PaperCard from "@/components/PaperCard";
import PaperViewer from "@/components/PaperViewer";
import EmptyState from "@/components/EmptyState";
import { useMeta } from "@/lib/useMeta";
import { fetchPaperBySlug, fetchRelatedPapers, bumpView, bumpCopy } from "@/lib/papers";
import { useTaxonomies } from "@/lib/taxonomies";
import { describeTags, tagsOf } from "@/lib/taxonomy";
import { formatDateBn, teacherOf, toBn } from "@/lib/format";
import { toneHue } from "@/lib/tone";
import { APP_URL } from "@/lib/config";
import { cn } from "@/lib/utils";

function DetailSkeleton() {
  return (
    <div className="container py-8" aria-busy="true">
      <div className="mb-6 animate-pulse space-y-3">
        <div className="h-5 w-28 rounded-full bg-muted" />
        <div className="h-8 w-3/4 rounded bg-muted" />
        <div className="h-4 w-1/3 rounded bg-muted" />
      </div>
      <div className="h-[30rem] animate-pulse rounded-2xl bg-muted" />
    </div>
  );
}

export default function PaperDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const taxonomies = useTaxonomies();

  const [paper, setPaper] = useState(null);
  const [failedSlug, setFailedSlug] = useState(null);
  const [related, setRelated] = useState({ slug: null, rows: [] });
  const [copying, setCopying] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchPaperBySlug(slug)
      .then((p) => {
        if (cancelled) return;
        setPaper(p);
        bumpView(slug);
        // একই শ্রেণি + একই বিষয়ের অন্য পেপার
        const sameClassSubject = tagsOf(p).filter((t) => t.startsWith("class:") || t.startsWith("subject:"));
        fetchRelatedPapers({ slug, tags: sameClassSubject })
          .then((rows) => !cancelled && setRelated({ slug, rows }))
          .catch(() => {});
      })
      .catch(() => !cancelled && setFailedSlug(slug));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const status = failedSlug === slug ? "not-found" : paper?.slug === slug ? "ready" : "loading";
  const ready = status === "ready" ? paper : null;

  const tags = ready ? describeTags(taxonomies, ready) : [];
  const metaLabels = ["class", "subject"].map((k) => tags.find((t) => t.key === k)?.label).filter(Boolean);

  useMeta({
    title: ready ? `${ready.title} — মুমতাহিন গ্যালারি` : "মুমতাহিন গ্যালারি",
    description: ready ? `${[...metaLabels, "প্রশ্নপত্র"].join(" · ")}, বিনামূল্যে কপি করুন।` : undefined,
  });

  const handleCopy = async () => {
    setCopying(true);
    // গণনার অনুরোধটাকে পাতা বদলানোর আগে যাওয়ার একটু সময় দেওয়া (সর্বোচ্চ ০.৬ সেকেন্ড) — নাহলে ব্রাউজার সেটা বাতিল করে দিতে পারে
    await Promise.race([bumpCopy(slug), new Promise((r) => setTimeout(r, 600))]);
    window.location.href = `${APP_URL}/import?source=gallery&paper=${encodeURIComponent(slug)}`;
    setTimeout(() => setCopying(false), 4000); // ব্যাক করে ফিরলে বাটন যেন আটকে না থাকে
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: ready.title, text: `${ready.title} — মুমতাহিন গ্যালারি`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2200);
    } catch {
      /* শেয়ার বাতিল করলে বা ব্রাউজার ক্লিপবোর্ড আটকালে — চুপচাপ */
    }
  };

  if (status === "not-found") {
    return (
      <div className="container py-16">
        <EmptyState
          icon={FileQuestion}
          title="এই প্রশ্নপত্রটা পাওয়া যায়নি"
          actions={
            <Button asChild>
              <Link to="/papers">অন্য প্রশ্নপত্র দেখুন</Link>
            </Button>
          }
        >
          লিংকটা ভুল হতে পারে, অথবা প্রশ্নপত্রটা সরিয়ে ফেলা হয়েছে।
        </EmptyState>
      </div>
    );
  }

  if (!ready) return <DetailSkeleton />;

  const teacher = teacherOf(ready);
  const canGoBack = location.key !== "default";
  const subjectHue = toneHue(tags.find((t) => t.key === "subject")?.value);
  const info = [
    ready.institution && { icon: Building2, label: "প্রতিষ্ঠান", value: ready.institution },
    teacher && { icon: User, label: "শেয়ার করেছেন", value: teacher },
    ready.created_at && { icon: CalendarDays, label: "শেয়ারের তারিখ", value: formatDateBn(ready.created_at) },
    { icon: CopyIcon, label: "কপি হয়েছে", value: `${toBn(ready.copy_count ?? 0)} বার` },
    { icon: Eye, label: "দেখা হয়েছে", value: `${toBn(ready.view_count ?? 0)} বার` },
  ].filter(Boolean);

  const copyLabel = copying ? "মূল অ্যাপ খোলা হচ্ছে…" : "নিজের একাউন্টে কপি করুন";

  return (
    <div className="container pb-28 pt-6 sm:pt-8 lg:pb-10">
      <div className="no-print mb-5">
        <Link
          to="/papers"
          onClick={(e) => {
            // তালিকা থেকে এসে থাকলে ঠিক সেই ফিল্টার করা তালিকাতেই ফেরা
            if (canGoBack) {
              e.preventDefault();
              navigate(-1);
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-lg py-1 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {canGoBack ? "ফিরে যান" : "সব প্রশ্নপত্র"}
        </Link>
      </div>

      <div className="detail-grid lg:grid lg:grid-cols-[minmax(0,1fr)_21rem] lg:items-start lg:gap-8">
        <div className="min-w-0">
          <header className="no-print mb-5 sm:mb-6" style={{ "--tone-h": subjectHue }}>
            <div className="tone mb-3 flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <Link
                  key={`${t.key}:${t.value}`}
                  to={`/papers?${encodeURIComponent(t.key)}=${encodeURIComponent(t.value)}`}
                  className={cn(
                    "rounded-full px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    t.key === "subject"
                      ? "bg-[var(--tone-bg)] font-medium text-[var(--tone-ink)] hover:bg-[var(--tone-bg-strong)]"
                      : "border border-border bg-card text-foreground hover:border-primary/50",
                  )}
                >
                  {t.label}
                </Link>
              ))}
            </div>
            <h1 className="font-display text-2xl font-bold leading-snug text-foreground sm:text-3xl">{ready.title}</h1>
            {(ready.institution || teacher) && (
              <p className="mt-2 text-muted-foreground">
                {[ready.institution, teacher && `শেয়ার করেছেন ${teacher}`].filter(Boolean).join(" · ")}
              </p>
            )}
          </header>

          {/* এক্সপোর্ট/প্রিন্ট বাটন PaperViewer-এর ভেতরে (আসল প্রিভিউর সাথে বান্ডেল করা) */}
          <div className="paper-desk">
            <PaperViewer paper={ready.paper_json} />
          </div>
        </div>

        <aside className="no-print mt-6 space-y-4 lg:sticky lg:top-24 lg:mt-0" aria-label="প্রশ্নপত্রের তথ্য">
          {/* ডেস্কটপের অ্যাকশন কার্ড — মোবাইলে নিচের আটকানো বার */}
          <div className="hidden rounded-2xl border border-border bg-card p-5 shadow-card lg:block">
            <h2 className="font-display text-base font-semibold text-foreground">কাজে লাগবে?</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              কপি করলে প্রশ্নপত্রটা আপনার নিজের একাউন্টে চলে যাবে — সেখানে নিজের মতো বদলে নিয়ে ছাপতে পারবেন।
            </p>
            <Button size="lg" className="mt-4 w-full" onClick={handleCopy} disabled={copying}>
              {copying ? <Loader2 className="animate-spin" aria-hidden="true" /> : <CopyIcon aria-hidden="true" />}
              {copyLabel}
            </Button>
            <Button variant="outline" className="mt-2 w-full" onClick={handleShare}>
              {linkCopied ? <Check aria-hidden="true" /> : <Share2 aria-hidden="true" />}
              {linkCopied ? "লিংক কপি হয়েছে" : "লিংক শেয়ার করুন"}
            </Button>
          </div>

          <dl className="space-y-3.5 rounded-2xl border border-border bg-card p-5">
            {info.map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-3">
                <Icon className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0">
                  <dt className="text-xs text-muted-foreground">{label}</dt>
                  <dd className="break-words text-sm text-foreground">{value}</dd>
                </div>
              </div>
            ))}
          </dl>
        </aside>
      </div>

      {related.slug === slug && related.rows.length > 0 && (
        <section className="no-print mt-12" aria-label="সম্পর্কিত প্রশ্নপত্র">
          <h2 className="mb-4 font-display text-xl font-bold text-foreground">একই ধরনের আরও প্রশ্নপত্র</h2>
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            {related.rows.map((p) => (
              <PaperCard key={p.id} paper={p} />
            ))}
          </div>
        </section>
      )}

      {/* মোবাইলে বুড়ো আঙুলের নাগালে প্রধান কাজ */}
      <div className="no-print pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 px-4 pt-3 backdrop-blur-md lg:hidden">
        <div className="mx-auto flex max-w-xl gap-2">
          <Button size="lg" className="flex-1" onClick={handleCopy} disabled={copying}>
            {copying ? <Loader2 className="animate-spin" aria-hidden="true" /> : <CopyIcon aria-hidden="true" />}
            {copyLabel}
          </Button>
          <Button size="lg" variant="outline" className="w-12 shrink-0 px-0" onClick={handleShare} aria-label={linkCopied ? "লিংক কপি হয়েছে" : "লিংক শেয়ার করুন"}>
            {linkCopied ? <Check aria-hidden="true" /> : <Share2 aria-hidden="true" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
